// Formatting for the interface's language: `tag` is a BCP 47 tag, English by default.

const cached = <T>(create: (tag: string) => T) => {
  const made = new Map<string, T>();
  return (tag: string): T => {
    let value = made.get(tag);
    if (!value) {
      value = create(tag);
      made.set(tag, value);
    }
    return value;
  };
};

const numberFormat = cached((tag) => new Intl.NumberFormat(tag));
const clockFormat = cached(
  (tag) => new Intl.DateTimeFormat(tag, { hour: 'numeric', minute: '2-digit' }),
);
const dateFormat = cached((tag) => new Intl.DateTimeFormat(tag, { month: 'long', day: 'numeric' }));
const fullDateFormat = cached(
  (tag) => new Intl.DateTimeFormat(tag, { year: 'numeric', month: 'long', day: 'numeric' }),
);
const unitFormat = cached(
  (key) =>
    new Intl.NumberFormat(key.split('|')[0], {
      style: 'unit',
      unit: key.split('|')[1],
      unitDisplay: 'narrow',
    }),
);

export const formatNumber = (value: number, tag = 'en'): string => numberFormat(tag).format(value);

/** Compact durations for countdowns: "42s", "3m 5s", "1h 20m" in English. */
export function formatDuration(totalSeconds: number, tag = 'en'): string {
  const seconds = Math.max(0, Math.ceil(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  const unit = (value: number, name: string) => unitFormat(`${tag}|${name}`).format(value);
  if (hours > 0)
    return minutes > 0 ? `${unit(hours, 'hour')} ${unit(minutes, 'minute')}` : unit(hours, 'hour');
  if (minutes > 0) {
    return rest > 0
      ? `${unit(minutes, 'minute')} ${unit(rest, 'second')}`
      : unit(minutes, 'minute');
  }
  return unit(rest, 'second');
}

/** "$5", "$12.50": whole amounts without decimals. `amount` is in cents. */
export function formatPrice(amount: number, currency: string, tag = 'en'): string {
  const value = amount / 100;
  return new Intl.NumberFormat(tag, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value);
}

/** An installer's size, in whole megabytes from 10 MB up: "98 MB", "4.2 MB". */
export function formatFileSize(bytes: number, tag = 'en'): string {
  const megabytes = bytes / 1_000_000;
  return new Intl.NumberFormat(tag, {
    style: 'unit',
    unit: 'megabyte',
    maximumFractionDigits: megabytes < 10 ? 1 : 0,
  }).format(megabytes);
}

/** A day in local time, such as "October 21". */
export const formatDate = (iso: string, tag = 'en'): string =>
  dateFormat(tag).format(new Date(iso));

/** A day in local time, with its year: "October 21, 2026". */
export const formatFullDate = (iso: string, tag = 'en'): string =>
  fullDateFormat(tag).format(new Date(iso));

/** Whether a moment falls on today, in local time. */
export const isToday = (iso: string, now = new Date()): boolean =>
  new Date(iso).toDateString() === now.toDateString();

/**
 * When something was saved, in local time, as closely as it's useful: the time today, the day
 * this year, and the day with its year before then.
 */
export function formatSavedAt(iso: string, now = new Date(), tag = 'en'): string {
  const saved = new Date(iso);
  if (isToday(iso, now)) return clockFormat(tag).format(saved);
  return (saved.getFullYear() === now.getFullYear() ? dateFormat : fullDateFormat)(tag).format(
    saved,
  );
}

/**
 * When a daily allowance resets, in local time: "7:00 PM", or "3:00 AM tomorrow" when that
 * falls on the next local day. Resets are always less than a day away.
 */
export function formatResetTime(
  iso: string,
  now = new Date(),
  tag = 'en',
  tomorrow = (time: string) => `${time} tomorrow`,
): string {
  const reset = new Date(iso);
  const time = clockFormat(tag).format(reset);
  return reset.toDateString() === now.toDateString() ? time : tomorrow(time);
}
